export const meta = {
	slug: 'bedhead-3d',
	title: 'Bedhead in 3D: the same map, plus depth',
	tag: 'Attractors',
	excerpt:
		'Bedhead is a 2D sine–cosine map with a third z step that lifts iterates into a copper-to-gold point cloud — discrete samples, not an ODE trail.',
	dateLabel: 'Aug 2026',
	readMinutes: 4,
	featured: true,
	thumbSlug: 'bedhead_attractor_3d',
	order: 4,
}


Bedhead is Ivan Emrich’s discrete map: sine and cosine mixed through `x` and `y`, two real knobs, no integrating rates. The familiar [flat Bedhead cloud](/bedhead_attractor) lives in the plane; here a third update pulls the same iterates into depth.

Classical Chaos draws that volume as copper-to-gold points — one sample per iterate, not a Lorenz-style continuous trail.

<VizEmbed slug="bedhead_attractor_3d" label="Bedhead 3D" animateN />

## Three discrete steps

Still no `dt`. Each sample is the next point on the map:

<BlockMath math={'x_{n+1} = \\sin(x y / b)\\, y + \\cos(a x - y)'} />
<BlockMath math={'y_{n+1} = x + \\sin(y)/b'} />
<BlockMath math={'z_{n+1} = y + \\cos(y x)/b'} />

The first two lines are the 2D Bedhead rule. The third was added for depth: `z` leans on the current `y` and the product `xy`, still divided by `b`.

```ts
x = Math.sin((x * y) / b) * y + Math.cos(a * x - y)
y = x + Math.sin(y) / b
z = y + Math.cos(y * x) / b
```

`b` is a denominator, so the stage floors it away from zero. Extreme samples get clamped so a bad pair does not blow the camera.

## Point cloud, not a butterfly trail

Lorenz and Aizawa integrate rates into a line. Bedhead 3D drops each iterate as a point, like the flat Bedhead cloud, then how many points are lit decides how far the pile has grown. The planar map gets a longer walk-through in the [Bedhead notebook](/blog/bedhead).

<Callout to="/bedhead_attractor_3d">
	<strong>Goto full canvas</strong>
	Retune `a` and `b` and the cloud rebuilds from the origin. Scrub iterate count, try a preset, drag to rotate.
</Callout>
